/**
 * capabilities.ts
 * Shared Zod contracts for the capability catalog endpoint (AZ-7 / CAT-CAP-3).
 *
 * Used by:
 *   - Backend: GET /platform/capabilities response serialisation
 *   - Frontend: typing the API response from getCapabilityCatalog()
 *
 * ZERO Hono/Drizzle/infrastructure imports — Zod only.
 * Note: this package cannot import @suite/kernel (contracts must be dependency-free
 * at the infrastructure level). The Zod shape mirrors CapabilityCatalogEntry.
 *
 * Spec: KI-31 / CAT-CAP-3 / AZ-7.2
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// CapabilityCatalogEntry — mirrors kernel CapabilityCatalogEntry interface
// ---------------------------------------------------------------------------

export const CapabilityCatalogEntrySchema = z.object({
  /** The exact capability key string (e.g. 'locx:read'). */
  key: z.string(),
  /** Module or sanctioned namespace owning this capability. */
  moduleKey: z.string(),
  /** Short human-readable label for display in the role editor. */
  label: z.string(),
  /** Longer description for the capability picker tooltip. */
  description: z.string(),
  /**
   * nomi-autoservicio-empleado Fase 1 (design D5) — catalog vs. assignable split.
   * Omitted ⇒ true (assignable via role edit / per-user grant). `false` ⇒ the key
   * is VALID for requireCapability() but REJECTED (422) when submitted through a
   * role write or a per-user grant write — used for every derived `self.*` key.
   */
  assignable: z.boolean().optional(),
});

export type CapabilityCatalogEntry = z.infer<typeof CapabilityCatalogEntrySchema>;

// ---------------------------------------------------------------------------
// CapabilityCatalogResponse — GET /platform/capabilities response (AZ-7.2)
// ---------------------------------------------------------------------------

export const CapabilityCatalogResponseSchema = z.object({
  capabilities: z.array(CapabilityCatalogEntrySchema),
});

export type CapabilityCatalogResponse = z.infer<typeof CapabilityCatalogResponseSchema>;
